'use client';

import Image from 'next/image';
import { BiCheckCircle, BiSolidStar, BiStar } from 'react-icons/bi';
import { useTranslation } from '@/hooks/useTranslation';
import LouiseButton from '@/components/buttons/LouiseButton';
import LouiseWatchNowButton, { WatchButtonCtaTargetType } from '@/components/buttons/LouiseWatchNowButton';
import { useModal } from '@/components/ModalContext/ModalContext';
import { GrCompliance } from 'react-icons/gr';

type Props = { locale: string };

export default function BuildSmarter({ locale }: Props) {
  const { t } = useTranslation(locale);
  const { openModal } = useModal();

  const steps = [
    {
      title: t('BuildSmarter/step1/Title'),
      description: t('BuildSmarter/step1/Description'),
      button: t('BuildSmarter/step1/Button'),
      imagePosition: 'left',
      imageUrl: 'https://cdn.1cdn.app/application/LOUISEBOT/25070719521861_scenario_blocks.png',
      features: [t('BuildSmarter/step1/feature1'), t('BuildSmarter/step1/feature2'), t('BuildSmarter/step1/feature3')],
      badge: t('BuildSmarter/step1/badge'),
      metrics: {
        value: '5 min',
        label: t('BuildSmarter/step1/metric'),
      },
      ctaTarget: 'discover-chatbot-type',
    },
    {
      title: t('BuildSmarter/step2/Title'),
      description: t('BuildSmarter/step2/Description'),
      button: t('BuildSmarter/step2/Button'),
      imagePosition: 'right',
      imageUrl: 'https://cdn.1cdn.app/application/LOUISEBOT/25020812324357_ai_clone-min.png',
      features: [t('BuildSmarter/step2/feature1'), t('BuildSmarter/step2/feature2'), t('BuildSmarter/step2/feature3')],
      badge: t('BuildSmarter/step2/badge'),
      metrics: {
        value: '24/7',
        label: t('BuildSmarter/step2/metric'),
      },
      ctaTarget: 'external',
    },
  ];

  const handleOpenDemoModal = (ctaTarget: string) => {
    switch (ctaTarget) {
      case 'discover-chatbot-type':
        openModal('DISCOVER_CHATBOT_TYPE', {});
        return;
      default:
        return;
    }
  };

  return (
    <section className="tw-relative tw-pt-16 lg:tw-pt-20 tw-pb-10  tw-overflow-hidden  tw-bg-transparent>">
      <div className="tw-relative tw-z-10 tw-container tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-max-w-7xl">
        {/* Section Header */}
        <div className="tw-text-center tw-mb-16 lg:tw-mb-20">
          <div className="tw-inline-flex tw-items-center tw-gap-2 tw-bg-gradient-to-r tw-from-blue-100 tw-to-purple-100 tw-text-blue-700 tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-medium tw-mb-6">
            <GrCompliance className="tw-w-4 tw-h-4" />
            {t('BuildSmarter/section-badge')}
          </div>
          <h2 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-5xl tw-font-bold tw-text-gray-900 tw-mb-4 tw-font-['Lexend']">
            {t('BuildSmarter/main-title')}
          </h2>
          <p className="tw-text-lg tw-text-gray-600 tw-max-w-3xl tw-mx-auto tw-font-['Lexend']">
            {t('BuildSmarter/main-description')}
          </p>
        </div>

        {/* Steps */}
        <div className="tw-space-y-20 lg:tw-space-y-32">
          {steps.map((step, index) => (
            <article
              key={index}
              className={`tw-grid lg:tw-grid-cols-2 tw-gap-8 lg:tw-gap-16 tw-items-center ${
                index % 2 === 0 ? '' : 'lg:tw-grid-flow-dense'
              }`}
            >
              {/* Image Section */}
              <div className={`tw-relative tw-group ${index % 2 === 0 ? '' : 'lg:tw-col-start-2'}`}>
                {/* Badge overlay */}
                <div className="tw-absolute tw-top-4 tw-left-4 tw-z-20">
                  <span className="tw-inline-flex tw-items-center tw-gap-1 tw-bg-white/90 tw-backdrop-blur-sm tw-text-blue-700 tw-px-3 tw-py-1 tw-rounded-full tw-text-sm tw-font-semibold tw-shadow-lg">
                    <BiStar className="tw-w-4 tw-h-4" />
                    {step.badge}
                  </span>
                </div>

                {/* Main Image */}
                <div className="tw-relative tw-overflow-hidden tw-rounded-3xl tw-shadow-2xl tw-transition-all tw-duration-500 group-hover:tw-shadow-3xl group-hover:tw-scale-[1.02]">
                  <Image
                    priority={index === 0}
                    quality={100}
                    className="tw-w-full tw-h-auto tw-object-cover"
                    width={700}
                    height={700}
                    src={step.imageUrl}
                    alt={`${step.title} - Feature showcase`}
                  />
                  {/* Overlay gradient */}
                  <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-black/20 tw-to-transparent tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity tw-duration-300"></div>
                </div>

                {/* Floating metric card */}
                <div className="tw-absolute tw--bottom-6 tw--right-6 tw-bg-white tw-rounded-2xl tw-p-4 tw-shadow-lg tw-border tw-border-gray-100">
                  <div className="tw-text-2xl tw-font-bold tw-text-blue-600 tw-font-['Lexend']">
                    {step.metrics.value}
                  </div>
                  <div className="tw-text-sm tw-text-gray-600 tw-font-['Lexend']">{step.metrics.label}</div>
                </div>
              </div>

              {/* Content Section */}
              <div
                className={`tw-space-y-6 lg:tw-space-y-8 ${
                  index % 2 === 0 ? '' : 'lg:tw-col-start-1 lg:tw-row-start-1'
                }`}
              >
                {/* Title */}
                <div className="tw-space-y-4">
                  <h3 className="tw-text-3xl sm:tw-text-4xl lg:tw-text-5xl tw-font-bold tw-text-gray-900 tw-font-['Lexend'] tw-leading-tight">
                    {step.title}
                  </h3>
                  <p className="tw-text-sm tw-text-gray-600 tw-leading-relaxed tw-font-['Lexend']">
                    {step.description}
                  </p>
                </div>

                {/* Features List */}
                <ul className="tw-space-y-3">
                  {step.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="tw-flex tw-items-start tw-gap-3">
                      <BiCheckCircle className="tw-w-6 tw-h-6 tw-text-green-500 tw-flex-shrink-0 tw-mt-0.5" />
                      <span className="tw-text-gray-700 tw-font-['Lexend'] tw-leading-relaxed">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Section */}
                <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-justify-start tw-gap-4 tw-pt-4">
                  <LouiseButton color="blue" cta={step.button} ctaTarget="register" gradiant={true} locale={locale} />
                  <LouiseWatchNowButton
                    textColor="gray"
                    cta={t('BuildSmarter/watch-demo')}
                    ctaTarget={step.ctaTarget as WatchButtonCtaTargetType}
                    locale={locale}
                    href="https://louisebot.com/en-US/chat/10743599-470e-4ebb-8bcb-abacebf3e70d"
                    onClick={() => handleOpenDemoModal(step.ctaTarget)}
                  />
                </div>

                {/* Social proof */}
                {index === 0 && (
                  <div className="tw-flex tw-items-center tw-gap-6 tw-pt-6 tw-border-t tw-border-gray-200">
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <div className="tw-flex">
                        {[...Array(5)].map((_, i) => (
                          <BiSolidStar key={i} className="tw-w-5 tw-h-5 tw-text-yellow-400" />
                        ))}
                      </div>
                      <span className="tw-text-gray-600 tw-font-semibold tw-font-['Lexend']">
                        {t('BuildSmarter/rating')}
                      </span>
                    </div>
                    <div className="tw-text-sm tw-text-gray-500 tw-font-['Lexend']">
                      {t('BuildSmarter/testimonial')}
                    </div>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
